import React, {useCallback, useState} from "react";
import {Card, CardBody, Col, Container, Row} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableTgBots from "./components/TableTgBots";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {TgBotsService} from "../../services/tg_bots.service";
import NoSearchResult from "../../components/Common/NoSearchResult";
import ServerPagination from "../../components/Common/ServerPagination";
import ModalTgBot from "./components/ModalTgBot";
import usePermissions from "../../hooks/usePermissions";
import {Navigate} from "react-router-dom";

interface ModalState {
    isModalOpen: boolean;
    id: number | null;
}

const TgBots = () => {
    document.title = "Telegram Bots | Dashboard";

    const {hasPermission} = usePermissions();
    if (!hasPermission("view-telegram-bots")) return <Navigate to="/no-access"/>

    const queryClient = useQueryClient();

    const [modal, setModal] = useState<ModalState>({isModalOpen: false, id: null});
    const [filter, setFilter] = useState({per_page: 10, page: 1, sort_key: "id", sort_order: "desc", q: ""});

    const {
        data: tgBots,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ["keyTgBots", filter],
        queryFn: () => TgBotsService.getAll(filter),
    });

    const onSuccessRemove = () => {
        queryClient.invalidateQueries({queryKey: ["keyTgBots", filter]})
    }

    const {mutate: removeTgBot} = useMutation({
        mutationFn: (id: number) => TgBotsService.remove(id),
        onSuccess: onSuccessRemove,
        mutationKey: ["keyRemoveTgBot"],
    });

    const updateFilter = useCallback((changes: Partial<typeof filter>) => {
        setFilter((prevFilter) => ({...prevFilter, ...changes}));
    }, [setFilter]);

    const handlePageSizeChange = useCallback((newPageSize: number) => updateFilter({
        per_page: newPageSize,
        page: 1
    }), [updateFilter])

    const handlePageChange = useCallback((newPage: number) => updateFilter({page: newPage}), [updateFilter])

    const handleSearchChange = useCallback((search: string) => {
        if (!search && !filter.q) return
        updateFilter({q: search, page: 1})
    }, [filter.q, updateFilter])

    const handleSortChange = useCallback((sortKey?: string, sortOrder?: string) => {
        updateFilter({sort_key: sortKey, sort_order: sortOrder, page: 1})
    }, [updateFilter])

    const handleModalSuccess = () => {
        queryClient.invalidateQueries({queryKey: ["keyTgBots", filter]})
            .then(() => setModal({isModalOpen: false, id: null}));
    }

    const handleAddClick = () => {
        setModal({isModalOpen: true, id: null});
    }

    const handleModalClose = () => {
        setModal({isModalOpen: false, id: null});
    }

    const handleUpdateClick = (id: number) => {
        setModal({isModalOpen: true, id});
    }

    const handleRemoveClick = (id: number) => {
        removeTgBot(id)
    }

    return (
        <div className="page-content">
            <Container fluid>
                {/* Render Breadcrumbs */}
                <Breadcrumbs title="Dashboard" breadcrumbItem="Telegram Bots"/>

                {hasPermission("create-telegram-bots") &&
                    <Row className="align-items-center">
                        <Col md={12}>
                            <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-3">
                                <button type="button" className="btn btn-light" onClick={handleAddClick}>
                                    <i className="bx bx-plus me-1"></i> Add New
                                </button>
                            </div>
                        </Col>
                    </Row>}

                <Row>
                    <Col lg="12">
                        <Card>
                            <CardBody className="border-bottom">
                                <TableTgBots
                                    data={tgBots?.data || []}
                                    pageSize={filter.per_page}
                                    onSearchChange={handleSearchChange}
                                    onPageSizeChange={handlePageSizeChange}
                                    onSortChange={handleSortChange}
                                    onUpdate={handleUpdateClick}
                                    onRemove={handleRemoveClick}
                                />

                                {!isLoading && tgBots?.data?.length === 0 && (<NoSearchResult/>)}
                                {isLoading && (<NoSearchResult text="Loading..."/>)}
                                {isError && (<NoSearchResult/>)}

                                {tgBots && tgBots.pagination.total_page > 1 && (
                                    <ServerPagination
                                        totalPages={tgBots.pagination.total_page}
                                        page={tgBots.pagination.current_page}
                                        onChange={handlePageChange}
                                        className="mt-2"/>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {modal.isModalOpen &&
                    <ModalTgBot
                        tgBotId={modal.id}
                        isOpen={modal.isModalOpen}
                        onSuccess={handleModalSuccess}
                        onClose={handleModalClose}/>}
            </Container>
        </div>
    )
}

export default TgBots;